import React, { useEffect, useState } from "react";
import { Button, Modal } from "antd";

/**
 * Inactivity warning modal (spec §5-6).
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 *
 * The countdown derives from the `deadline` timestamp on every tick (never a
 * decrementing counter), so background-tab timer throttling cannot drift it.
 * The page behind stays visible and untouched — Stay Logged In must not
 * reload or reroute anything.
 */

export interface SessionExpiryModalProps {
  open: boolean;
  /** ms-epoch at which the session idles out. */
  deadline: number;
  /** Stay Logged In — refresh + reset idle; resolves when done. */
  onStay: () => Promise<void>;
  onSignOut: () => void;
}

const formatRemaining = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const SessionExpiryModal: React.FC<SessionExpiryModalProps> = ({
  open,
  deadline,
  onStay,
  onSignOut,
}) => {
  const [remainingMs, setRemainingMs] = useState(() => deadline - Date.now());
  const [staying, setStaying] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setRemainingMs(deadline - Date.now());
    const timer = window.setInterval(() => {
      setRemainingMs(deadline - Date.now());
    }, 500);
    return () => window.clearInterval(timer);
  }, [open, deadline]);

  const handleStay = async () => {
    setStaying(true);
    try {
      await onStay();
    } finally {
      setStaying(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Session expiring"
      closable={false}
      maskClosable={false}
      keyboard={false}
      zIndex={2000}
      footer={[
        <Button key="signout" onClick={onSignOut} disabled={staying}>
          Sign out
        </Button>,
        <Button key="stay" type="primary" loading={staying} onClick={handleStay}>
          Stay logged in
        </Button>,
      ]}
    >
      <p>Your session is about to expire due to inactivity.</p>
      <p>
        Session expires in <strong>{formatRemaining(remainingMs)}</strong>
      </p>
    </Modal>
  );
};

export default SessionExpiryModal;
